/**
 * Filter / content — List and Pill presentations.
 *
 * Pill: triggers move into a shared tablist; one panel + media active.
 * Arrow desktop: hovering an item or focusing its title activates it; clicking
 * the active item follows its link, which is the title link when set and
 * otherwise the body CTA.
 * Arrow mobile: accordion — one expanded item; row toggles; the expanded card
 * follows the same link. An item without a link is never clickable.
 */
(() => {
  const TAG = 'filter-content';
  const DESKTOP_QUERY = '(min-width: 768px)';
  const INTERACTIVE_QUERY =
    'a, button, input, select, textarea, summary, [role="button"], [role="link"]';

  // Only button-styled links stand in for the item. Links inside prose would
  // send the whole-item click somewhere the merchant did not intend.
  const BODY_CTA_QUERY =
    ':scope > [data-filter-body] .cta a[href], :scope > [data-filter-body] a.button[href]';

  // Opening a body moves the titles below it, so a switch is only honoured
  // once the pointer has rested on one item.
  const PREVIEW_INTENT_DELAY = 100;

  class FilterContent extends HTMLElement {
    #items = [];
    #pillTriggers = [];
    #arrowTriggers = [];
    #mobileToggles = [];
    #triggersHost = null;
    #mediaHost = null;
    #mediaQuery = window.matchMedia(DESKTOP_QUERY);
    #resizeObserver = new ResizeObserver(() => this.#syncBodyHeights());
    #activeId = null;
    #pointerX = null;
    #pointerY = null;
    #landedId = null;
    #holdNextItem = false;
    #intentId = null;
    #intentTimer = null;

    connectedCallback() {
      this.#triggersHost = this.querySelector('[data-filter-triggers]');
      this.#mediaHost = this.querySelector('[data-filter-media]');
      this.#items = Array.from(this.querySelectorAll('[data-filter-item]'));

      if (this.#items.length === 0) return;

      this.#collectTriggers();
      this.#syncPanelRoles();
      this.#bindEvents();
      this.#mediaQuery.addEventListener('change', this.#handleMediaQueryChange);
      this.#resizeObserver.observe(this);
      this.#observeBodyContent();
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);

      const initial =
        this.#items.find((item) => item.classList.contains('is-active')) || this.#items[0];

      this.classList.add('is-ready');

      // The mobile accordion opens on tap, so nothing is expanded on load.
      this.#activate(initial.getAttribute('data-block-id'), { expand: false });
      this.#syncModeLayout();
    }

    disconnectedCallback() {
      for (const trigger of [...this.#pillTriggers, ...this.#arrowTriggers]) {
        trigger.removeEventListener('click', this.#handleTriggerClick);
        trigger.removeEventListener('keydown', this.#handleKeydown);
        trigger.removeEventListener('focus', this.#handleTriggerFocus);
      }

      this.removeEventListener('pointermove', this.#handlePointerMove);
      this.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#resetPreviewState();

      for (const toggle of this.#mobileToggles) {
        toggle.removeEventListener('click', this.#handleMobileToggle);
      }

      for (const item of this.#items) {
        item.removeEventListener('click', this.#handleItemClick);
      }

      this.#mediaQuery.removeEventListener('change', this.#handleMediaQueryChange);
      this.#resizeObserver.disconnect();
      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    get #layoutMode() {
      return this.dataset.layoutMode || 'arrow';
    }

    get #isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    get #isDesktop() {
      return this.#mediaQuery.matches;
    }

    #collectTriggers() {
      this.#pillTriggers = [];
      this.#arrowTriggers = [];
      this.#mobileToggles = [];

      for (const item of this.#items) {
        const pill = this.#findPill(item);
        const arrow = item.querySelector(':scope > [data-trigger-style="arrow"]');
        const mobileToggle = item.querySelector(':scope > [data-filter-mobile-toggle]');

        if (pill) {
          this.#pillTriggers.push(pill);
        }

        if (arrow) {
          this.#arrowTriggers.push(arrow);
        }

        if (mobileToggle) {
          this.#mobileToggles.push(mobileToggle);
        }
      }

      if (this.#triggersHost) {
        const hosted = this.#layoutMode === 'pill' ? this.#pillTriggers : [];

        this.#triggersHost.replaceChildren(...hosted);
      }

      if (this.#pillTriggers[0]) {
        this.#pillTriggers[0].classList.add('is-active');
      }

      if (this.#arrowTriggers[0]) {
        this.#arrowTriggers[0].classList.add('is-active');
      }
    }

    #syncPanelRoles() {
      if (this.#layoutMode !== 'pill') return;

      for (const item of this.#items) {
        const trigger = this.#findPill(item);

        item.setAttribute('role', 'tabpanel');

        if (trigger?.id) {
          item.setAttribute('aria-labelledby', trigger.id);
        }
      }
    }

    #findPill(item) {
      const owned = item.querySelector(':scope > [data-trigger-style="pill"]');

      if (owned) return owned;

      // Pill mode moves triggers into the shared tablist, so re-collecting has
      // to look there rather than under the item they came from.
      const blockId = item.getAttribute('data-block-id');

      return this.#triggersHost?.querySelector(
        `[data-trigger-style="pill"][data-block-id="${blockId}"]`
      );
    }

    #bindEvents() {
      // Arrow key navigation belongs to the tablist. Arrow titles are ordinary
      // links and buttons, where those keys should still scroll the page.
      for (const trigger of this.#pillTriggers) {
        trigger.addEventListener('click', this.#handleTriggerClick);
        trigger.addEventListener('keydown', this.#handleKeydown);
      }

      for (const trigger of this.#arrowTriggers) {
        trigger.addEventListener('click', this.#handleTriggerClick);
      }

      for (const toggle of this.#mobileToggles) {
        toggle.addEventListener('click', this.#handleMobileToggle);
      }

      for (const item of this.#items) {
        item.addEventListener('click', this.#handleItemClick);
      }

      this.#syncPreviewListeners();
    }

    #syncModeLayout() {
      this.dataset.viewport = this.#isDesktop ? 'desktop' : 'mobile';
      this.#syncPreviewListeners();
      this.#syncMediaPlacement();
    }

    #handleMediaQueryChange = () => {
      this.#syncModeLayout();

      if (!this.#activeId) return;

      const active = this.#items.find(
        (item) => item.getAttribute('data-block-id') === this.#activeId
      );

      // Carry the accordion's open state across the breakpoint rather than
      // letting it spring open on resize.
      this.#activate(this.#activeId, {
        expand: active?.classList.contains('is-expanded') === true,
      });
    };

    #syncPreviewListeners() {
      const usePreview =
        this.#layoutMode === 'arrow' && this.#isDesktop && !this.#isDesignMode;

      this.removeEventListener('pointermove', this.#handlePointerMove);
      this.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#resetPreviewState();

      for (const trigger of this.#arrowTriggers) {
        trigger.removeEventListener('focus', this.#handleTriggerFocus);

        if (usePreview) {
          // Titles are links, so Enter navigates. Focus has to preview the
          // panel or keyboard users cannot read an item without leaving.
          trigger.addEventListener('focus', this.#handleTriggerFocus);
        }
      }

      if (usePreview) {
        this.addEventListener('pointermove', this.#handlePointerMove);
        this.addEventListener('pointerleave', this.#handlePointerLeave);
      }
    }

    #handleTriggerFocus = (event) => {
      const blockId = event.currentTarget.getAttribute('data-block-id');

      if (!blockId || blockId === this.#activeId) return;

      this.#commitPreview(blockId);
    };

    /**
     * Hovering is read from movement rather than pointerenter, because opening
     * a body slides the titles below it: the boundary crossings that follow
     * are the layout catching up with the cursor, not a choice of item.
     */
    #handlePointerMove = (event) => {
      const isStill = event.clientX === this.#pointerX && event.clientY === this.#pointerY;

      this.#pointerX = event.clientX;
      this.#pointerY = event.clientY;

      // Chrome re-runs hover after a layout shift by dispatching a move at the
      // unchanged cursor position, so only a change of coordinates counts.
      if (isStill) return;

      const item = event.target.closest('[data-filter-item]');
      const blockId = item?.getAttribute('data-block-id');

      // A pointer that had not moved yet has no known position, so its first
      // move only reveals where it was already resting. Whatever it finds is
      // held rather than read as a move onto it.
      if (this.#holdNextItem) {
        this.#holdNextItem = false;
        this.#landedId = blockId && blockId !== this.#activeId ? blockId : null;
        this.#clearIntent();
        return;
      }

      // The item the last switch slid under the cursor is held back until the
      // pointer reaches a different one, so a switch cannot chain into the
      // neighbour it pushed beneath the cursor.
      if (this.#landedId) {
        if (!blockId || blockId === this.#landedId) {
          this.#clearIntent();
          return;
        }

        this.#landedId = null;
      }

      // Hovering covers the whole item, so reading the open body is not a
      // switch and the gaps between items hold whatever is already active.
      if (!blockId || blockId === this.#activeId) {
        this.#clearIntent();
        return;
      }

      if (blockId === this.#intentId) return;

      this.#clearIntent();
      this.#intentId = blockId;
      this.#intentTimer = setTimeout(() => this.#commitPreview(blockId), PREVIEW_INTENT_DELAY);
    };

    #handlePointerLeave = () => {
      this.#pointerX = null;
      this.#pointerY = null;
      this.#resetPreviewState();
    };

    #commitPreview(blockId) {
      this.#clearIntent();

      if (blockId === this.#activeId) return;

      this.#activate(blockId);
      this.#holdLandedItem(blockId);
    }

    /**
     * A body that opens shorter than the one it replaced pulls the titles below
     * it up, so an item the buyer never aimed at can come to rest under the
     * cursor. Whichever item lands there is recorded so hovering it counts only
     * once the pointer has left and returned deliberately.
     */
    #holdLandedItem(blockId) {
      this.#landedId = null;

      // A cursor resting on the section reports neither hover nor coordinates
      // until it moves, so a keyboard preview cannot tell what it left beneath
      // the pointer. That is deferred to the first move instead.
      this.#holdNextItem = this.#pointerX === null;

      if (this.#holdNextItem) return;

      const landed = document
        .elementFromPoint(this.#pointerX, this.#pointerY)
        ?.closest('[data-filter-item]');

      if (!landed || !this.#items.includes(landed)) return;

      const landedId = landed.getAttribute('data-block-id');

      if (landedId && landedId !== blockId) {
        this.#landedId = landedId;
      }
    }

    #clearIntent() {
      clearTimeout(this.#intentTimer);
      this.#intentTimer = null;
      this.#intentId = null;
    }

    #resetPreviewState() {
      this.#clearIntent();
      this.#landedId = null;
      this.#holdNextItem = false;
    }

    #handleTriggerClick = (event) => {
      const trigger = event.currentTarget;
      const blockId = trigger.getAttribute('data-block-id');
      const style = trigger.getAttribute('data-trigger-style');
      const link = trigger.getAttribute('data-link');

      if (style === 'arrow' && !this.#isDesktop) {
        event.preventDefault();

        const item = this.#items.find((entry) => entry.getAttribute('data-block-id') === blockId);

        if (item?.classList.contains('is-expanded')) {
          item.classList.remove('is-expanded');
          const mobileToggle = item.querySelector(':scope > [data-filter-mobile-toggle]');

          if (mobileToggle) {
            mobileToggle.setAttribute('aria-expanded', 'false');
          }

          this.#syncMediaPlacement();
          this.#focusRow(item, 'toggle');
          return;
        }

        this.#activate(blockId, { expand: true });
        this.#focusRow(item, 'title');
        return;
      }

      if (style === 'arrow' && this.#isDesktop && link && !this.#isDesignMode) {
        const isModifiedClick =
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

        // Modified clicks open a new tab/window, so leave the current panel alone.
        if (!isModifiedClick) {
          this.#activate(blockId);
        }

        return;
      }

      // Without its own link the title is a button that only reveals the item,
      // so once the item is open the title follows the body CTA like the rest
      // of the content does. Checking before activating keeps the first click
      // on a pointerless device a reveal rather than a navigation.
      if (style === 'arrow' && this.#isDesktop && !link && !this.#isDesignMode) {
        event.preventDefault();

        const item = this.#items.find((entry) => entry.getAttribute('data-block-id') === blockId);
        const target = item?.classList.contains('is-active') ? this.#itemLink(item) : null;

        if (target) {
          this.#followLink(target, event);
          return;
        }

        this.#activate(blockId);
        return;
      }

      event.preventDefault();
      this.#activate(blockId);
    };

    /**
     * Where the item points: its own title link when the merchant set one,
     * otherwise the CTA in its body. Reusing an existing link means the wider
     * hit area adds no second focus target and no nested link.
     */
    #itemLink(item) {
      return (
        item.querySelector(':scope > a[data-trigger-style="arrow"]') ||
        item.querySelector(BODY_CTA_QUERY)
      );
    }

    #followLink(link, event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        window.open(link.href, '_blank', 'noopener');
        return;
      }

      link.click();
    }

    /**
     * Extends the open item's hit area to its whole content, so the body,
     * image, and surrounding space follow the same link as the CTA. Desktop
     * widens the active item; mobile widens the expanded card.
     */
    #handleItemClick = (event) => {
      const item = event.currentTarget;

      if (this.#layoutMode !== 'arrow' || this.#isDesignMode) return;

      const isOpen = this.#isDesktop
        ? item.classList.contains('is-active')
        : item.classList.contains('is-expanded');

      if (!isOpen) return;

      // Controls inside the item keep their own behaviour: the CTA navigates
      // itself, and the title stays the trigger that reveals or collapses.
      if (event.target.closest(INTERACTIVE_QUERY)) return;

      // A drag that selected text was not meant as a click.
      if (window.getSelection()?.toString()) return;

      const target = this.#itemLink(item);

      if (target) {
        this.#followLink(target, event);
      }
    };

    #handleMobileToggle = (event) => {
      event.preventDefault();
      const blockId = event.currentTarget.getAttribute('data-block-id');
      const item = this.#items.find((entry) => entry.getAttribute('data-block-id') === blockId);

      if (!item) return;

      if (item.classList.contains('is-active') && item.classList.contains('is-expanded')) {
        item.classList.remove('is-expanded');
        event.currentTarget.setAttribute('aria-expanded', 'false');
        return;
      }

      this.#activate(blockId, { expand: true });
      this.#focusRow(item, 'title');
    };

    /**
     * Expanding and collapsing hides the control that was just activated, so
     * focus has to follow to its replacement or it falls back to the body.
     */
    #focusRow(item, control) {
      const selector =
        control === 'title'
          ? ':scope > [data-trigger-style="arrow"]'
          : ':scope > [data-filter-mobile-toggle]';

      item.querySelector(selector)?.focus();
    }

    #handleKeydown = (event) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

      if (!keys.includes(event.key)) return;

      const triggers = this.#pillTriggers;

      if (!triggers.includes(event.currentTarget)) return;

      event.preventDefault();

      const currentIndex = triggers.indexOf(event.currentTarget);
      const lastIndex = triggers.length - 1;
      let nextIndex = currentIndex;

      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      } else {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      }

      const next = triggers[nextIndex];

      next.focus();
      this.#activate(next.getAttribute('data-block-id'));
    };

    #handleBlockSelect = (event) => {
      if (!this.#isDesignMode || !this.contains(event.target)) return;

      const item = event.target.closest('[data-filter-item]');

      if (!item || !this.contains(item)) return;

      this.#activate(item.getAttribute('data-block-id'), { expand: true });
    };

    #activate(blockId, options = {}) {
      if (!blockId) return;

      const { expand = !this.#isDesktop && this.#layoutMode === 'arrow' } = options;

      this.#activeId = blockId;

      for (const trigger of [...this.#pillTriggers, ...this.#arrowTriggers]) {
        const isActive = trigger.getAttribute('data-block-id') === blockId;

        trigger.classList.toggle('is-active', isActive);
      }

      // Selection state and roving tabindex belong to the tablist only.
      for (const trigger of this.#pillTriggers) {
        const isActive = trigger.getAttribute('data-block-id') === blockId;

        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      }

      for (const item of this.#items) {
        const isActive = item.getAttribute('data-block-id') === blockId;
        const mobileToggle = item.querySelector(':scope > [data-filter-mobile-toggle]');

        item.classList.toggle('is-active', isActive);

        // Drives the pointer affordance for the widened hit area.
        item.classList.toggle('is-linked', this.#itemLink(item) !== null);

        if (this.#layoutMode === 'pill') {
          item.setAttribute('tabindex', isActive ? '0' : '-1');
        }

        if (this.#layoutMode === 'arrow' && !this.#isDesktop) {
          const shouldExpand = isActive && expand;

          item.classList.toggle('is-expanded', shouldExpand);

          if (mobileToggle) {
            mobileToggle.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
          }

          item.hidden = false;
        } else if (this.#layoutMode === 'pill') {
          item.classList.remove('is-expanded');

          if (this.#isDesignMode) {
            item.hidden = false;
          } else {
            item.toggleAttribute('hidden', !isActive);
          }
        } else {
          item.classList.remove('is-expanded');
          item.hidden = false;

          if (mobileToggle) {
            mobileToggle.setAttribute('aria-expanded', 'false');
          }
        }
      }

      this.#syncBodyHeights();
      this.#syncMediaPlacement();
    }

    /**
     * Bodies are clipped to their measured height, so observing a body never
     * reports growth from inside it. Watching the unclipped children instead
     * catches late-loading images, font swaps, and nested blocks.
     */
    #observeBodyContent() {
      for (const item of this.#items) {
        const body = item.querySelector(':scope > [data-filter-body]');

        if (!body) continue;

        for (const child of body.children) {
          this.#resizeObserver.observe(child);
        }
      }
    }

    #syncBodyHeights() {
      const itemsHost = this.querySelector('[data-filter-items]');

      if (this.#layoutMode !== 'arrow' || !this.#isDesktop) {
        itemsHost?.style.removeProperty('--filter-content-items-min-height');
        return;
      }

      let maxBody = 0;

      for (const item of this.#items) {
        const body = item.querySelector(':scope > [data-filter-body]');

        if (!body) continue;

        maxBody = Math.max(maxBody, body.scrollHeight);
      }

      if (!itemsHost || this.#arrowTriggers.length === 0) return;

      let titlesHeight = 0;

      for (const trigger of this.#arrowTriggers) {
        titlesHeight += trigger.getBoundingClientRect().height;
      }

      const gap = parseFloat(getComputedStyle(itemsHost).rowGap) || 0;
      const bodyMargin = parseFloat(getComputedStyle(this).getPropertyValue('--spacing-xs')) || 0;
      const minHeight = titlesHeight + gap * (this.#arrowTriggers.length - 1) + maxBody + bodyMargin;
      const minHeightValue = `${Math.ceil(minHeight)}px`;

      if (itemsHost.style.getPropertyValue('--filter-content-items-min-height') !== minHeightValue) {
        itemsHost.style.setProperty('--filter-content-items-min-height', minHeightValue);
      }
    }

    #syncMediaPlacement() {
      if (!this.#mediaHost) return;

      const useMediaHost = this.#isDesktop || this.#layoutMode === 'pill';

      if (!useMediaHost) {
        this.#returnHostMedia();
        return;
      }

      for (const item of this.#items) {
        const media = item.querySelector(':scope > [data-filter-item-media]');

        if (media) {
          this.#mediaHost.appendChild(media);
        }
      }

      const hostedMedia = Array.from(
        this.#mediaHost.querySelectorAll(':scope > [data-filter-item-media]')
      );
      let hasActiveMedia = false;
      let activeMedia = null;

      for (const media of hostedMedia) {
        const isActive = media.getAttribute('data-owner-id') === this.#activeId;

        media.classList.toggle('is-active', isActive);
        media.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        hasActiveMedia ||= isActive;

        if (isActive) {
          activeMedia = media;
        }
      }

      this.#mediaHost.classList.toggle('has-active-media', hasActiveMedia);

      for (const viewport of ['desktop', 'mobile']) {
        const property = `--filter-content-image-ratio-${viewport}`;
        const ratio = activeMedia?.style.getPropertyValue(property);

        if (ratio) {
          this.#mediaHost.style.setProperty(property, ratio);
        } else {
          this.#mediaHost.style.removeProperty(property);
        }
      }
    }

    #returnHostMedia() {
      const hostedMedia = Array.from(
        this.#mediaHost?.querySelectorAll(':scope > [data-filter-item-media]') || []
      );

      for (const hosted of hostedMedia) {
        const ownerId = hosted.getAttribute('data-owner-id');
        const owner = this.#items.find((item) => item.getAttribute('data-block-id') === ownerId);

        hosted.classList.remove('is-active');
        hosted.removeAttribute('aria-hidden');

        if (owner) {
          owner.appendChild(hosted);
        }
      }

      this.#mediaHost?.classList.remove('has-active-media');
      this.#mediaHost?.style.removeProperty('--filter-content-image-ratio-desktop');
      this.#mediaHost?.style.removeProperty('--filter-content-image-ratio-mobile');
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, FilterContent);
  }
})();
